import React, { useEffect, useState } from "react";
import { Button, Dropdown, Menu, Spin, message } from "antd";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "./PDFReaderModal.css";
import axios from "axios";

const PDFReaderModal = ({
  isOpen,
  fileName,
  onClose,
  blockTime,
  showDownload = false,
  onDownloadPdf = () => {},
  onDownloadExcel = () => {},
}) => {
  const effectiveBlockTime = typeof blockTime === "number" ? blockTime : 10;
  const isBlockingEnabled = effectiveBlockTime > 0;

  const [remainingTime, setRemainingTime] = useState(effectiveBlockTime);
  const [timerActive, setTimerActive] = useState(isBlockingEnabled);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  // Simplified PDF streaming logic (no progress calculation)
  const fetchPdfFromS3 = async (retry = true) => {
    if (!fileName || !isOpen) return;

    setLoading(true);
    try {
      const streamingUrl = `${process.env.REACT_APP_API_BASE_URL}/course/api/client-api/streaming/pdf/${fileName}`;
      const response = await axios.get(streamingUrl, {
        responseType: "blob",
        headers: {
          Accept: "application/pdf",
          "Cache-Control": "no-cache",
        },
        withCredentials: true,
        timeout: 60000,
      });

      if (response.status !== 200 && response.status !== 206) {
        throw new Error(`Unexpected HTTP status: ${response.status}`);
      }

      // ✅ Accept both full and partial content responses
      if (response.status !== 200 && response.status !== 206) {
        throw new Error(`Unexpected HTTP status: ${response.status}`);
      }

      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(pdfBlob);
      setPdfBlobUrl(blobUrl);
    } catch (err) {
      console.error("PDF fetch error:", err);
      if(err.response?.status === 401 && retry){
        message.warning("Session expired. Attempting to refresh token...");
        alert("Session expired, please login again");
        localStorage.removeItem('authtoken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/';
        return Promise.reject(err);

      }
      else if (err.response?.status === 404) {
        message.error("PDF file not found on server.");
      } else if (err.response?.status === 500) {
        message.error("Server error while streaming PDF.");
      } else {
        message.error("Failed to load PDF. Please try again.");
      }
    } finally {
      setLoading(false);
      
    }
  };

  // 🔁 Fetch PDF whenever modal opens or filename changes
  useEffect(() => {
    if (isOpen && fileName) fetchPdfFromS3();
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }
    };
  }, [fileName, isOpen]);

  // ⏱️ Timer logic
  useEffect(() => {
    if (isOpen && isBlockingEnabled) {
      setRemainingTime(effectiveBlockTime);
      setTimerActive(true);
    } else if (isOpen && !isBlockingEnabled) {
      setRemainingTime(0);
      setTimerActive(false);
    }
  }, [isOpen, effectiveBlockTime, isBlockingEnabled]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (isBlockingEnabled) {
        setTimerActive(document.visibilityState === "visible");
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isBlockingEnabled]);

  useEffect(() => {
    let timer;
    if (timerActive && isBlockingEnabled && remainingTime > 0) {
      timer = setInterval(() => setRemainingTime((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [timerActive, remainingTime, isBlockingEnabled]);

  useEffect(() => {
    if (isOpen) {
      setIsFullScreen(false);
    }
  }, [isOpen]);

  //Download PDF
  const handleDownloadPdf = async () => {
    try {
      const streamingUrl = `${process.env.REACT_APP_API_BASE_URL}/course/api/client-api/streaming/pdf/${fileName}`;
      const response = await axios.get(streamingUrl, {
        responseType: "blob",
        headers: {
          Accept: "application/pdf",
          "Cache-Control": "no-cache",
        },
        withCredentials: true,
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("Download error:", error);
      message.error("Failed to download PDF.");
    }
  };

  const isCloseDisabled = isBlockingEnabled && remainingTime > 0;
  const toggleFullScreen = () => {
    const wrapper = document.querySelector(".pdf-viewer-wrapper-admin");
    if (!document.fullscreenElement) {
      wrapper.requestFullscreen?.();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullScreen(false);
    }
  };

  if (!isOpen) return null;

  const downloadMenu = (
    <Menu>
      <Menu.Item key="pdf" onClick={handleDownloadPdf}>
        Download as PDF
      </Menu.Item>
      <Menu.Item key="excel" onClick={onDownloadExcel}>
        Download as Excel
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="show-content-container">
      {/* Header */}
      <div className="pdf-controls-header">
        <div className="pdf-title">
          {isBlockingEnabled ? (
            <span>
              PDF Viewer {remainingTime > 0 && `(Close in ${remainingTime}s)`}
            </span>
          ) : (
            "PDF Viewer"
          )}
        </div>

        <div className="pdf-controls">
          {showDownload && (
            <Dropdown overlay={downloadMenu} placement="bottomRight">
              <Button size="small" type="default" className="pdf-control-btn">
                Download
              </Button>
            </Dropdown>
          )}
          <Button onClick={toggleFullScreen} className="pdf-control-btn">
            {isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
          </Button>
          <Button
            onClick={onClose}
            disabled={isCloseDisabled}
            className="pdf-close-btn"
            style={{ opacity: isCloseDisabled ? 0.5 : 1 }}
          >
            Close {remainingTime > 0 && `(${remainingTime}s)`}
          </Button>
        </div>
      </div>

      {/* Viewer */}
      <div className="pdf-viewer-content">
        {loading ? (
          <div className="pdf-loading-state">
            <Spin tip="Loading PDF..." size="large" />
            <div className="loading-text">Streaming PDF from S3...</div>
          </div>
        ) : pdfBlobUrl ? (
          <div className="pdf-viewer-wrapper-admin">
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <Viewer
                fileUrl={pdfBlobUrl}
                defaultScale={1.2}
                onDocumentLoadSuccess={() => {
                  console.log("PDF loaded successfully");
                }}
                onDocumentLoadFailure={(error) => {
                  console.error("PDF load failed:", error);
                  message.error("Failed to render PDF");
                }}
              />
            </Worker>
          </div>
        ) : (
          <div className="pdf-error-state">
            <div>No PDF loaded</div>
            <Button
              onClick={() => fetchPdfFromS3()}
              type="primary"
              className="retry-btn"
            >
              Retry Loading
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PDFReaderModal;